import { ApiResponse } from '@gps-hunt/shared';
import { useAuthStore } from '@/stores/authStore';
import { getIdToken } from '@/services/firebase/config';

/**
 * Determine API URL based on environment
 * - Uses VITE_API_URL env var if set
 * - Auto-detects Azure deployment and uses Function App URL
 * - Falls back to localhost for development
 */
function getApiUrl(): string {
  // Check for explicit env var first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Auto-detect Azure Static Web Apps deployment
  if (typeof window !== 'undefined' && window.location.hostname.includes('azurestaticapps.net')) {
    return 'https://gpshunt-func-dev.azurewebsites.net/api';
  }
  
  // Fallback to localhost for development
  return 'http://localhost:7071/api';
}

const API_URL = getApiUrl();

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * API client with automatic authentication and error handling
 */
class ApiClient {
  private baseUrl: string;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = useAuthStore.getState().token;
    console.log('[ApiClient] getAuthHeaders - token exists:', !!token, 'token length:', token?.length);
    if (!token) {
      console.warn('No auth token available - API calls will fail');
      return {};
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  private async refreshToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = getIdToken(true); // Force refresh from Firebase

    try {
      const newToken = await this.refreshPromise;
      if (newToken) {
        useAuthStore.getState().setToken(newToken);
        console.log('[ApiClient] Token refreshed successfully');
      }
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (!skipAuth) {
      const authHeaders = await this.getAuthHeaders();
      Object.assign(headers, authHeaders);
    }

    const url = `${this.baseUrl}${endpoint}`;
    console.log(`API ${fetchOptions.method || 'GET'} ${endpoint}`);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      // Handle 401 Unauthorized - token may have expired
      if (response.status === 401 && !skipAuth && !isRetry) {
        console.log('[ApiClient] Got 401 - attempting token refresh...');
        const newToken = await this.refreshToken();
        if (newToken) {
          // Retry the request with the new token
          return this.request<T>(endpoint, options, true);
        }
        // Token refresh failed - return the error
      }

      const data = await response.json();

      if (!response.ok) {
        console.error(`API error ${response.status}:`, data.error);
        return {
          success: false,
          error: data.error || {
            code: 'UNKNOWN_ERROR',
            message: 'An unknown error occurred',
          },
          timestamp: new Date().toISOString(),
        };
      }

      return data;
    } catch (error) {
      console.error('API network error:', error);
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
        timestamp: new Date().toISOString(),
      };
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { 
      ...options, 
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export const apiClient = new ApiClient(API_URL);

// Hunt API methods
export const huntApi = {
  create: (displayName: string) =>
    apiClient.post<{ huntId: string; joinCode: string }>('/hunts', {
      creatorDisplayName: displayName,
    }),

  join: (joinCode: string, displayName: string, role: 'hider' | 'hunter') =>
    apiClient.post<{ huntId: string }>(`/hunts/join`, {
      joinCode,
      displayName,
      role,
    }),

  get: (huntId: string) => apiClient.get<{ hunt: unknown }>(`/hunts/${huntId}`),

  start: (huntId: string) => apiClient.post(`/hunts/${huntId}/start`),

  markReady: (huntId: string) => apiClient.post(`/hunts/${huntId}/ready`),
};

// Pin API methods
export const pinApi = {
  create: (huntId: string, data: { latitude: number; longitude: number; accuracy: number; itemCount: number; hintPhoto?: string }) =>
    apiClient.post(`/hunts/${huntId}/pins`, data),

  list: (huntId: string) => apiClient.get(`/hunts/${huntId}/pins`),

  found: (huntId: string, pinId: string, itemsFound: number) =>
    apiClient.post(`/hunts/${huntId}/pins/${pinId}/found`, { itemsFound }),
};
